// ModernPageLayout.js - Wrapper pour unifier le design moderne sur toutes les pages
import React from 'react';

const ModernPageLayout = ({ 
  children, 
  title, 
  subtitle, 
  icon, 
  headerActions,
  background = 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
  padding = '24px 20px'
}) => {
  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: background,
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      position: 'relative',
      zIndex: 1
    }}>
      {/* Header moderne avec glassmorphism */}
      {(title || subtitle || icon) && (
        <div style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          padding: padding,
          background: 'rgba(15, 23, 42, 0.8)',
          backdropFilter: 'blur(30px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          zIndex: 999,
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            maxWidth: '1600px',
            margin: '0 auto'
          }}>
            <div style={{ flex: 1 }}>
              {icon && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: '16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  marginBottom: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  {icon}
                </div>
              )}
              {title && (
                <h1 style={{
                  fontSize: 'clamp(24px, 4vw, 32px)',
                  fontWeight: 700,
                  margin: icon ? '0 0 6px 0' : '0 0 6px 0',
                  letterSpacing: '-0.5px',
                  lineHeight: 1.2,
                  color: '#ffffff'
                }}>
                  {title}
                </h1>
              )}
              {subtitle && (
                <p style={{
                  fontSize: 'clamp(14px, 2vw, 16px)',
                  color: 'rgba(255, 255, 255, 0.7)',
                  margin: 0,
                  letterSpacing: '0.2px',
                  fontWeight: 500
                }}>
                  {subtitle}
                </p>
              )}
            </div>
            {headerActions && (
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                flexShrink: 0
              }}>
                {headerActions}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div style={{
        maxWidth: '1600px',
        margin: '0 auto',
        padding: padding,
        paddingTop: (title || subtitle || icon) ? '0' : padding.split(' ')[0]
      }}>
        {children}
      </div>
    </div>
  );
};

// Composant Card moderne réutilisable
export const ModernCard = ({ 
  children, 
  title, 
  icon, 
  gradient,
  shadow,
  onClick,
  style = {}
}) => {
  const cardStyle = {
    background: gradient || 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: shadow ? `0 8px 32px ${shadow}, 0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)` : '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    ...style
  };

  return (
    <div
      style={cardStyle}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = shadow 
            ? `0 12px 40px ${shadow}, 0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15)`
            : '0 8px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = cardStyle.boxShadow;
        }
      }}
    >
      {/* Overlay pour effet de profondeur */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
        pointerEvents: 'none',
        zIndex: 1
      }} />
      
      {title && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
          position: 'relative',
          zIndex: 2
        }}>
          {icon && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              {icon}
            </div>
          )}
          <h3 style={{
            fontSize: '18px',
            fontWeight: 700,
            margin: 0,
            color: '#ffffff',
            letterSpacing: '-0.3px'
          }}>
            {title}
          </h3>
        </div>
      )}
      
      <div style={{ position: 'relative', zIndex: 2 }}>
        {children}
      </div>
    </div>
  );
};

// Composant Badge moderne
export const ModernBadge = ({ 
  children, 
  variant = 'default',
  icon,
  style = {}
}) => {
  const variants = {
    default: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: '#ffffff',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    success: {
      background: 'rgba(16, 185, 129, 0.2)',
      color: '#10b981',
      border: '1px solid rgba(16, 185, 129, 0.3)'
    },
    warning: {
      background: 'rgba(245, 158, 11, 0.2)',
      color: '#f59e0b',
      border: '1px solid rgba(245, 158, 11, 0.3)'
    },
    danger: {
      background: 'rgba(239, 68, 68, 0.2)',
      color: '#ef4444',
      border: '1px solid rgba(239, 68, 68, 0.3)'
    },
    info: {
      background: 'rgba(59, 130, 246, 0.2)',
      color: '#3b82f6',
      border: '1px solid rgba(59, 130, 246, 0.3)'
    }
  };

  const variantStyle = variants[variant] || variants.default;

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      borderRadius: '12px',
      fontSize: '13px',
      fontWeight: 600,
      backdropFilter: 'blur(10px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
      ...variantStyle,
      ...style
    }}>
      {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
      <span>{children}</span>
    </div>
  );
};

// Palette de couleurs unifiée
export const colorPalette = {
  pointage: { gradient: 'linear-gradient(135deg, #00d4ff, #00a8cc)', shadow: 'rgba(0, 212, 255, 0.3)' },
  conges: { gradient: 'linear-gradient(135deg, #00e676, #00b85c)', shadow: 'rgba(0, 230, 118, 0.3)' },
  demandes: { gradient: 'linear-gradient(135deg, #ff6b35, #ff8c42)', shadow: 'rgba(255, 107, 53, 0.3)' },
  planning: { gradient: 'linear-gradient(135deg, #9c27b0, #ba68c8)', shadow: 'rgba(156, 39, 176, 0.3)' },
  frais: { gradient: 'linear-gradient(135deg, #ffc107, #ffd54f)', shadow: 'rgba(255, 193, 7, 0.3)' },
  equipe: { gradient: 'linear-gradient(135deg, #2196f3, #64b5f6)', shadow: 'rgba(33, 150, 243, 0.3)' },
  stats: { gradient: 'linear-gradient(135deg, #3f51b5, #7986cb)', shadow: 'rgba(63, 81, 181, 0.3)' },
  soldes: { gradient: 'linear-gradient(135deg, #06b6d4, #22d3ee)', shadow: 'rgba(6, 182, 212, 0.3)' },
  formations: { gradient: 'linear-gradient(135deg, #009688, #4db6ac)', shadow: 'rgba(0, 150, 136, 0.3)' },
  documents: { gradient: 'linear-gradient(135deg, #607d8b, #90a4ae)', shadow: 'rgba(96, 125, 139, 0.3)' },
  alertes: { gradient: 'linear-gradient(135deg, #f44336, #e57373)', shadow: 'rgba(244, 67, 54, 0.3)' },
  messages: { gradient: 'linear-gradient(135deg, #e91e63, #f06292)', shadow: 'rgba(233, 30, 99, 0.3)' },
  rapports: { gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', shadow: 'rgba(139, 92, 246, 0.3)' },
  reglages: { gradient: 'linear-gradient(135deg, #1e293b, #334155)', shadow: 'rgba(30, 41, 59, 0.3)' },
  aide: { gradient: 'linear-gradient(135deg, #6366f1, #818cf8)', shadow: 'rgba(99, 102, 241, 0.3)' },
  profil: { gradient: 'linear-gradient(135deg, #10b981, #34d399)', shadow: 'rgba(16, 185, 129, 0.3)' }
};

export default ModernPageLayout;


